import { useCallback, useEffect, useRef, useState } from "react";

export const useHttp = (config = {}) => {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(false);

  const {
    requestCallback = async () => {
      throw new Error("useHttp: requestCallback is required");
    },
    onLoad = () => {},
    onError = () => {},
  } = config;

  const mountedRef    = useRef(true);
  const controllerRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (controllerRef.current && typeof controllerRef.current.abort === "function") {
        controllerRef.current.abort();
      }
    };
  }, []);

  const cancel = useCallback(() => {
    if (controllerRef.current && typeof controllerRef.current.abort === "function") {
      controllerRef.current.abort();
    }
  }, []);

  const request = useCallback(
    async (data) => {
      // cancel any in-flight request
      cancel();

      const ac = new AbortController();
      controllerRef.current = ac;

      if (mountedRef.current) {
        setLoading(true);
        setError(false);
      }

      try {
        const res = await requestCallback(data, { signal: ac.signal });

        if (mountedRef.current) setLoading(false);
        // call safely (no optional chaining needed)
        onLoad(res);
        return res;
      } catch (err) {
        if (err && err.name === "AbortError") {
          // aborted: nothing else to do
          return;
        }
        if (mountedRef.current) {
          setLoading(false);
          setError(true);
        }
        onError(err);
        return err;
      } finally {
        if (controllerRef.current === ac) controllerRef.current = null;
      }
    },
    [cancel, requestCallback, onLoad, onError]
  );

  return { loading, error, request, cancel };
};
